import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// 服務資訊
export interface SelectedService {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration: number;
  category?: string;
}

// 選擇的日期時間
export interface SelectedDateTime {
  date: string; // YYYY-MM-DD
  time: string; // ISO string
  displayTime: string; // HH:mm
}

// 選擇的員工
export interface SelectedStaff {
  id: string;
  name: string;
  phone?: string;
  email?: string;
}

// 客戶資訊
export interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  note?: string;
  customerLineId?: string;
}

// 預約流程狀態
interface BookingFlowState {
  // 店家資訊
  storeId: string | null;
  storeName: string | null;

  // 選擇的資料
  service: SelectedService | null;
  dateTime: SelectedDateTime | null;
  staff: SelectedStaff | null;
  customerInfo: CustomerInfo | null;

  // 預約結果
  bookingId: string | null;
  managementToken: string | null;

  // Actions
  setStore: (storeId: string, storeName: string) => void;
  setService: (service: SelectedService) => void;
  setDateTime: (dateTime: SelectedDateTime) => void;
  setStaff: (staff: SelectedStaff | null) => void;
  setCustomerInfo: (info: CustomerInfo) => void;
  setBookingResult: (bookingId: string, managementToken: string) => void;

  // 清除狀態
  clearAll: () => void;
  clearBookingResult: () => void;

  // 驗證
  canProceedToDateTime: () => boolean;
  canProceedToStaff: () => boolean;
  canProceedToInfo: () => boolean;
  canSubmit: () => boolean;
}

const initialState = {
  storeId: null,
  storeName: null,
  service: null,
  dateTime: null,
  staff: null,
  customerInfo: null,
  bookingId: null,
  managementToken: null,
};

export const useBookingFlowStore = create<BookingFlowState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // 設定店家
      setStore: (storeId: string, storeName: string) => {
        set({ storeId, storeName });
      },

      // 設定服務
      setService: (service: SelectedService) => {
        set({ service });
      },

      // 設定日期時間
      setDateTime: (dateTime: SelectedDateTime) => {
        set({ dateTime });
      },

      // 設定員工
      setStaff: (staff: SelectedStaff | null) => {
        set({ staff });
      },

      // 設定客戶資訊
      setCustomerInfo: (customerInfo: CustomerInfo) => {
        set({ customerInfo });
      },

      // 設定預約結果
      setBookingResult: (bookingId: string, managementToken: string) => {
        set({ bookingId, managementToken });
      },

      // 清除所有狀態
      clearAll: () => {
        set(initialState);
      },

      // 清除預約結果（保留選擇資料，方便再次預約）
      clearBookingResult: () => {
        set({
          bookingId: null,
          managementToken: null,
        });
      },

      // 驗證：是否可以進入日期時間選擇頁
      canProceedToDateTime: () => {
        const { storeId, service } = get();
        return !!(storeId && service);
      },

      // 驗證：是否可以進入員工選擇頁
      canProceedToStaff: () => {
        const { storeId, service, dateTime } = get();
        return !!(storeId && service && dateTime);
      },

      // 驗證：是否可以進入資訊填寫頁
      canProceedToInfo: () => {
        const { storeId, service, dateTime } = get();
        // 員工選擇是可選的，所以不需要驗證 staff
        return !!(storeId && service && dateTime);
      },

      // 驗證：是否可以提交預約
      canSubmit: () => {
        const { storeId, service, dateTime, customerInfo } = get();
        return !!(
          storeId &&
          service &&
          dateTime &&
          customerInfo &&
          customerInfo.name &&
          customerInfo.email &&
          customerInfo.phone
        );
      },
    }),
    {
      name: 'booking-flow-storage',
      partialize: (state) => ({
        storeId: state.storeId,
        storeName: state.storeName,
        service: state.service,
        dateTime: state.dateTime,
        staff: state.staff,
        customerInfo: state.customerInfo,
      }),
    }
  )
);
